<template>
  <div class="informations-contract">
    <slide-up-modal
      v-if="cancelationRequested"
      ref="contractCancelationModal"
      modal-id="contractCancelationModal"
      modal-title="Annulation de contrat"
      @modal-closed="hideCancelationModal"
    >
      <p>
        Êtes-vous certain de vouloir annuler le contrat de domiciliation
        de l'agence <strong>{{ contract.offer.distributed_product.agency.display_name }}</strong> ?
      </p>

      <p class="alert alert-warning">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> Si une autorisation est associée à la commande de ce contrat, celle-ci sera annulée.
      </p>

      <div class="actions" style="margin-top: 20px;">
          <button class="btn btn-complete btn-sm" @click="performContractCancelation">Valider</button>
          <button class="btn btn-default btn-sm" @click="hideCancelationModal">Annuler</button>
      </div>
    </slide-up-modal>

    <div class="contract-details">
      <hr>
      <div class="row">
        <div class="col-md-4 left-row">
          <img v-if='isFetched'
               :src="imagePathOf"
               height="300px"
               style="max-width:100%; object-fit: cover;"/>
          <div v-if="!imageFetched" class="progress-circle-indeterminate progress-circle-success" ></div>
        </div>

        <div class="col-md-4">
          <ul class="contract-address">
            <li>
              <i class="circle" :class="classDependingOnContractStatus"></i>
              {{ contract.decoded_type }} {{ contract.organization_display_name }}
              
              <div class="pull-right">
                <a v-if="!contract.completed"
                  :href="completeButtonUrl" 
                  tag="button" 
                  class="btn btn-primary btn-sm" 
                  target="_blank" 
                >
                  <span>Compléter</span>
                </a>

                <router-link v-if="contract.validated"
                  :to="routerLink"
                  tag="button"
                  class="btn btn-default btn-sm">Gérer</router-link>
                  
                <button v-if="!contract.completed"
                  class="btn btn-primary btn-sm"
                  @click="displayCancelationModal"
                >Annuler</button>
              </div>
            </li>
            <li>{{ agencyAddressLine }}</li>
            <li style="margin-top: 10px;">Type : {{ contract.decoded_domiciliation_type }}</li>
            <!-- <li>N° : 293994404 ???</li> -->
            <li>Statut : {{ contract.decoded_status }}</li>
            <li v-if="contract.start_date">Date de début : {{ contract.start_date | moment-date }}</li>
            <li v-if="contract.end_date">Date de sortie : {{ contract.end_date | moment-date }}</li>
            <li v-if="contract.order && contract.order.sap_promo_code" style="margin-top: 10px;">
              Code promo : {{ contract.order.sap_promo_code }}
            </li>
          </ul>

          <div v-if="contract.contract_options.length">
            <hr>

            <div class="top-options">
              <span>Options :</span>
            </div>

            <ul class="options">
              <li v-for="contract_option in contract.contract_options"
                  :key="'contract-option-'+contract_option.id">
                {{decodeOptionLine(contract_option)}}
              </li>
            </ul>

            <div class="table-responsive" v-if="hasMailForwardedOption">
              <div id="basicTable_wrapper"
                  class="dataTables_wrapper form-inline no-footer">
                <table class="table table-hover table-borderless dataTable no-footer"
                      id="basicTable"
                      role="grid"
                      style="margin: 0;">
                  <tbody>
                    <tr>
                      <td class="v-align-top">
                        <span>Réexpedition actuelle</span>
                      </td>
                      <td class="v-align-top">
                        <ul v-if="activeForwardingAddress">
                          <li>{{activeForwardingAddress.street_line1}}</li>
                          <li v-if="activeForwardingAddress.street_line2">{{activeForwardingAddress.street_line2}}</li>
                          <li>{{activeForwardingAddress.zipcode}} {{activeForwardingAddress.city}}</li>
                          <li v-if="activeForwardingAddress.decoded_state">{{activeForwardingAddress.decoded_state}}</li>
                          <li v-if="activeForwardingAddress.decoded_country">{{activeForwardingAddress.decoded_country}}</li>
                          <li v-if="activeForwardingAddress.end_date">Jusqu'au {{activeForwardingAddress.end_date}}</li>
                          <!-- <li>Jour de réexpedition: tous ???</li> -->
                          <li v-if="activeForwardingAddress.comments">Commentaire: {{activeForwardingAddress.comments}}</li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <hr>
          
          <ul class="status-flags">
            <li>
              <i class="fa"
                 :class="[handleContractFlag('package_reception')]"></i>Colis</li>
            <li>
              <i class="fa"
                 :class="[handleContractFlag('postal')]"></i>Procuration</li>
            <li>
              <i class="fa"
                 :class="[handleContractFlag('registered_letter_calls')]"></i>Notif. si LR</li>
            <li>
              <i class="fa"
                 :class="[handleContractFlag('mail_blocked')]"></i>Courrier bloqué</li>
          </ul>
        </div>

        <div class="col-md-4">
          <digital-signature v-if="contract"
            :contract-id="contract.id"
            :customer-id="contract.customer_id"
            parent-component="customerInformationsTab"
          ></digital-signature>
        </div>
      </div>
    </div>

    <div v-if="contract.start_date == contract.end_date" class="item-overlay"></div>
  </div>
</template>

<script>
  import SlideUpModal from 'components/Modals/SlideUpModal'
  import commonLogic from './commonLogic'
  import contractsMixin from 'mixins/contracts'
  import DigitalSignature from './DigitalSignature'
  import { mapActions, mapState } from 'vuex'

  export default {
    components: {
      SlideUpModal,
      DigitalSignature
    },

    mixins: [commonLogic, contractsMixin],

    data () {
      return {
          imageFetched: false,
          cancelationRequested: false,
          hasMailForwardedOption: false
      }
    },

    computed: {
      ...mapState('customer', [
        'contracts',
        'forwardingAddresses'
      ]),

      ...mapState('contract', ['fullOffers']),

      activeForwardingAddress () {
        return this.forwardingAddresses && this.forwardingAddresses.active
      },

      isFetched () {
        return _.find(this.fullOffers, (offer) => offer.id == this.contract.offer.id) != undefined
      },

      imagePathOf () {
        var offer = _.find(this.fullOffers, (offer) => offer.id == this.contract.offer.id)

        if (offer != undefined) {
          var img = _.find(offer.distributed_product.agency.images, (image) => image.order_column == 1)
          if (img != undefined) {
              this.imageFetched = true
              return __API_URL__ + "/photos/" + img.id + "/" + img.file_name
          }
        }
      },

      classDependingOnContractStatus () {
          switch (this.contract.status) {
              case 'active':
                return 'green'
                break
              case 'terminated':
                return 'red'
                break
              case 'pending-startup':
                return 'blue'
                break
              case 'pending-termination':
                return 'orange'
                break
          }
      },
    },

    methods: {
      handleContractFlag (name) {
        return this.contractWithInstruction(this.contract, name) ? "fa-check" : "fa-close"
      },

      findMailOption () {
        return _.find(
          this.contract.contract_options,
          (contractOption) => contractOption.option.mail_kept || contractOption.option.mail_forwarded
        )
      },

      ...mapActions('customer', [
        'fetchForwardingAddress',
      ]),

      ...mapActions('contract', ['fetchSingleFullOffer', 'cancelContract']),

      displayCancelationModal() {
        this.cancelationRequested = true
      },

      hideCancelationModal() {
        this.cancelationRequested = false
      },

      performContractCancelation() {
        this.$refs.contractCancelationModal.isLoading = true

        this.cancelContract(this.contract.id)
          .then(() => this.cancelationRequested = false)
          .catch(() => {
            this.$refs.contractCancelationModal.isLoading = false
          })
      }
    },

    // watch: {
    //   contract (contract) {
    //     if (_.isEmpty(contract)) return;
    //     const contractMailOption = this.findMailOption()
    //     // debugger
    //     this.isMailForwarded = contractMailOption.option.mail_forwarded
    //     const contractId = this.contract.id,
    //       contractMailOptionId = contractMailOption.id
    //
    //     if (this.isMailForwarded)
    //       this.fetchForwardingAddress({contractId, contractMailOptionId})
    //     else
    //       console.info('no forwarding address for id: ', this.contract.id)
    //   }
    // },

    created () {
      console.log(`checking contract forwarding addresses for id: ${this.contract.id} ...`)
      // if (!_.isEmpty(this.contract))

        this.fetchSingleFullOffer(this.contract.offer.id)
      const unwatchContracts = this.$watch(
        'contract',
        (contract) => {
          if (_.isEmpty(contract)) return;
          const contractMailOption = this.findMailOption()
          if (!contractMailOption) return;

          this.hasMailForwardedOption = contractMailOption.option.mail_forwarded
          const contractId = this.contract.id,
            contractMailOptionId = contractMailOption.id

          if (this.hasMailForwardedOption)
            this.fetchForwardingAddress({ contractId, contractMailOptionId })
          else
            console.info('no forwarding address for id: ', this.contract.id)

          // this.fetchSingleFullOffer(contract.offer.id)

        }, {
          immediate: true
        }
      )
    },
  }
</script>

<style lang="scss" scoped>
  .informations-contract {
    position: relative;

    .item-overlay {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.75);
      z-index: 1010;
    }
  }

  .progress-circle-success {
    position: absolute;
    top: 110px;
    left: 50%;
    transform: translate(-50%, -50%);
  }
</style>



// WEBPACK FOOTER //
// client/components/Customer/InformationsContent/Contracts/Domiciliation.vue